export function toRoleSlug(role) {
  return role
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-');
}

export const DEFAULT_CRITERIA = [
  { label: 'Relevant experience', desc: 'Has practical experience applicable to this role' },
  { label: 'Communication', desc: 'Communicates clearly and professionally, written and verbal' },
  { label: 'Reliability', desc: 'Demonstrates punctuality, follow-through, and accountability' },
  { label: 'Team fit', desc: 'Works well with others and contributes positively to team culture' },
  { label: 'Problem solving', desc: 'Identifies issues and takes appropriate action without over-escalating' },
];

const CRITERIA_LIBRARY = {
  'cafe-manager': [
    { label: 'Leadership and team management', desc: 'Demonstrates ability to lead, motivate, and develop a cafe team' },
    { label: 'Service standards', desc: 'Has clear standards for customer experience and can maintain them across shifts' },
    { label: 'Operational knowledge', desc: 'Understands opening/closing procedures, stock management, and daily operations' },
    { label: 'Problem-solving under pressure', desc: 'Stays calm and effective during busy periods or unexpected issues' },
    { label: 'Food safety awareness', desc: 'Familiar with food hygiene requirements and can ensure compliance' },
    { label: 'Financial literacy', desc: 'Comfortable with basic cost control, wastage, and margin awareness' },
  ],
  'barista': [
    { label: 'Coffee craft and technique', desc: 'Demonstrates strong espresso extraction, milk texturing, and consistency' },
    { label: 'Customer service', desc: 'Warm, professional, and able to build rapport during service' },
    { label: 'Speed and efficiency', desc: 'Can maintain quality output during high-volume periods' },
    { label: 'Cleanliness and hygiene', desc: 'Maintains clean workspace and follows food safety practices' },
    { label: 'Team contribution', desc: 'Supports colleagues and communicates clearly during service' },
  ],
  'chef': [
    { label: 'Culinary skill and technique', desc: 'Demonstrates strong cooking fundamentals across a range of dishes' },
    { label: 'Menu and product knowledge', desc: 'Understands ingredients, dietary requirements, and allergen management' },
    { label: 'Kitchen leadership', desc: 'Can lead a section or team with clarity and calmness' },
    { label: 'Food safety and hygiene', desc: 'Thorough knowledge of HACCP and kitchen safety standards' },
    { label: 'Consistency under pressure', desc: 'Maintains quality output during busy service periods' },
    { label: 'Cost management', desc: 'Understands wastage, portion control, and GP management' },
  ],
  'retail-assistant': [
    { label: 'Customer service', desc: 'Friendly, approachable, and genuinely helpful with customers' },
    { label: 'Product knowledge', desc: 'Willing to learn product range and make relevant recommendations' },
    { label: 'Store presentation', desc: 'Maintains tidy, well-stocked, and well-presented shop floor' },
    { label: 'Reliability and availability', desc: 'Punctual, dependable, and flexible with rostering' },
    { label: 'Cash handling and POS', desc: 'Comfortable processing transactions accurately' },
  ],
  'sales-assistant': [
    { label: 'Sales technique', desc: 'Uses active listening and questioning to identify customer needs' },
    { label: 'Customer rapport', desc: 'Builds trust quickly and creates a positive buying experience' },
    { label: 'Product knowledge', desc: 'Learns product range and uses it confidently in sales conversations' },
    { label: 'Resilience', desc: 'Handles objections and rejection without losing motivation' },
    { label: 'Target orientation', desc: 'Motivated by goals and tracks their own performance' },
  ],
  'builder': [
    { label: 'Trade skill and quality', desc: 'Demonstrates high standard of workmanship relevant to the trade' },
    { label: 'Health and safety', desc: 'Follows site safety rules and can identify hazards proactively' },
    { label: 'Plans and specifications', desc: 'Can read and interpret construction drawings accurately' },
    { label: 'Reliability and punctuality', desc: 'Consistently on time and ready to work' },
    { label: 'Communication on site', desc: 'Works well with supervisors and other trades' },
  ],
  'labourer': [
    { label: 'Physical capability', desc: 'Fit for the physical demands of the role' },
    { label: 'Health and safety awareness', desc: 'Understands and follows site safety requirements' },
    { label: 'Following instructions', desc: 'Can take clear direction and complete tasks without over-supervision' },
    { label: 'Reliability', desc: 'Consistent attendance and punctuality' },
    { label: 'Team contribution', desc: 'Works cooperatively and supports team output' },
  ],
  'office-administrator': [
    { label: 'Organisational skills', desc: 'Manages multiple tasks, deadlines, and priorities effectively' },
    { label: 'Attention to detail', desc: 'Produces accurate work and catches errors before they cause problems' },
    { label: 'Communication skills', desc: 'Professional written and verbal communication with internal and external contacts' },
    { label: 'Systems proficiency', desc: 'Comfortable with Microsoft Office, Google Workspace, or similar tools' },
    { label: 'Initiative and proactivity', desc: 'Anticipates needs and acts without waiting to be asked' },
  ],
  'driver': [
    { label: 'Driving safety and record', desc: 'Clean licence and strong safety-first mindset' },
    { label: 'Route planning and time management', desc: 'Plans effectively to meet delivery windows' },
    { label: 'Customer interaction', desc: 'Professional and courteous with customers during deliveries' },
    { label: 'Vehicle care', desc: 'Completes pre-trip checks and reports issues promptly' },
    { label: 'Physical capability', desc: 'Able to handle loading and unloading requirements of the role' },
  ],
  'support-worker': [
    { label: 'Genuine care and empathy', desc: 'Demonstrates authentic commitment to the wellbeing of those they support' },
    { label: 'Professional boundaries', desc: 'Maintains appropriate boundaries while being warm and connected' },
    { label: 'Communication and documentation', desc: 'Records accurately and communicates clearly with the team' },
    { label: 'Resilience and composure', desc: 'Stays calm and effective in emotionally demanding situations' },
    { label: 'Reliability', desc: 'Consistent, dependable, and accountable — critical in a care role' },
  ],
  'childcare-worker': [
    { label: 'Child-centred practice', desc: 'Demonstrates genuine warmth, patience, and respect for children' },
    { label: 'Curriculum knowledge', desc: 'Understanding of Te Whāriki and how to apply it in practice' },
    { label: 'Family communication', desc: 'Communicates with parents and whanau in a welcoming and informative way' },
    { label: 'Safety and supervision', desc: 'Maintains active supervision and responds quickly to safety concerns' },
    { label: 'Documentation', desc: 'Observes, records, and shares children\'s learning meaningfully' },
  ],
};

const SLUG_ALIASES = {
  'cafe-manager': ['cafe-manager', 'coffee-shop-manager', 'cafe-supervisor'],
  'barista': ['barista', 'coffee-maker', 'coffee-specialist'],
  'chef': ['chef', 'head-chef', 'sous-chef', 'kitchen-manager', 'cook', 'kitchen-hand'],
  'retail-assistant': ['retail-assistant', 'retail-associate', 'shop-assistant', 'store-assistant', 'retail-sales-assistant'],
  'sales-assistant': ['sales-assistant', 'sales-associate', 'sales-consultant', 'sales-representative'],
  'builder': ['builder', 'carpenter', 'construction-worker', 'plumber', 'electrician', 'painter', 'roofer', 'tiler'],
  'labourer': ['labourer', 'laborer', 'general-labourer', 'site-labourer', 'factory-worker', 'warehouse-worker'],
  'office-administrator': ['office-administrator', 'administrator', 'admin', 'receptionist', 'office-manager', 'executive-assistant', 'personal-assistant', 'pa', 'coordinator', 'office-coordinator'],
  'driver': ['driver', 'delivery-driver', 'truck-driver', 'courier', 'van-driver'],
  'support-worker': ['support-worker', 'carer', 'care-worker', 'aged-care-worker', 'disability-support-worker', 'health-support-worker'],
  'childcare-worker': ['childcare-worker', 'ece-teacher', 'teacher-aide', 'early-childhood-teacher', 'daycare-worker', 'childcare-assistant'],
};

function resolveSlug(slug) {
  for (const [canonical, aliases] of Object.entries(SLUG_ALIASES)) {
    if (aliases.some((alias) => slug.includes(alias) || alias.includes(slug))) {
      return canonical;
    }
  }
  return null;
}

export function getCriteriaForRole(roleSlug) {
  const resolved = resolveSlug(roleSlug);
  if (resolved && CRITERIA_LIBRARY[resolved]) {
    return CRITERIA_LIBRARY[resolved];
  }
  return DEFAULT_CRITERIA;
}
